import {Injectable, Type} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {ReactionApiModel} from '../../../../../api/models/reaction.api.model';
import {QueryParamsApiModel} from '../../../../../api/models/query-params-api.model';
import {ResultListApiModel} from '../../../../../api/models/result-list.api.model';
import {
  ReactedSubjectInterface,
  ReactionListItemInterface
} from '../../../../../core/interfaces/reaction-list-item.interface';
import {UserRestService} from '../../../../../api/services/user.rest.service';
import {SocialListAdapter, SocialListLoadResult} from '../social-list-adapter.interface';
import {SocialListModalConfig} from '../social-list-modal-config.interface';
import {SocialListType} from '../social-list-type.type';
import {ReactionListItemComponent} from '../items/reaction-list-item.component';
import {ReactionListItemsBuilderService} from '../builders/reaction-list-items-builder.service';
import {ReactionListCacheFacade} from '../reaction-list-cache.facade';

type UserReactionsSubject = ReactedSubjectInterface;

@Injectable({providedIn: 'root'})
export class UserReactionsSocialListAdapter implements SocialListAdapter<UserReactionsSubject, never, ReactionListItemInterface> {
  public readonly type: SocialListType = 'user-reactions';
  public readonly itemComponent: Type<ReactionListItemComponent> = ReactionListItemComponent;
  public readonly emptyTranslationKey = 'shared.modal.reaction_list.empty';
  public readonly trimmedTranslationKey = 'shared.modal.reaction_list.trimmed';
  public readonly titleTranslationKey = 'shared.modal.modal_service.reactions.title';

  constructor(
    private readonly userRS: UserRestService,
    private readonly reactionListItemsBuilder: ReactionListItemsBuilderService,
    private readonly reactionListCacheFacade: ReactionListCacheFacade,
  ) {
  }

  public loadPage(
    config: SocialListModalConfig<UserReactionsSubject>,
    page: number,
    pageSize: number
  ): Observable<SocialListLoadResult<ReactionListItemInterface>> {
    const params = new QueryParamsApiModel();
    params.page = page;
    params.limit = pageSize;
    params.pagination = true;

    return this.reactionListCacheFacade.loadPage$(
      {listType: this.type, subjectId: config.subject['@id']},
      page,
      pageSize,
      () => this.userRS.getAggregatedReceivedLikesSubResourceCollection(config.subject['@id'], params).pipe(
        map((resultListAM: ResultListApiModel<ReactionApiModel>) => resultListAM?.records || [])
      ),
      config.initialData
    ).pipe(
      mergeMap(({reactions, hasMore, loadedPageCount}) => this.reactionListItemsBuilder.buildItems$(reactions, config.subject.apiModel).pipe(
        map((items) => ({items, hasMore, loadedPageCount}))
      )),
      catchError(() => of({items: [], hasMore: false}))
    );
  }

  public mergeItems(existingItems: ReactionListItemInterface[], incomingItems: ReactionListItemInterface[]): ReactionListItemInterface[] {
    return this.reactionListItemsBuilder.mergeItems(existingItems, incomingItems);
  }

  public trackBy(index: number, item: ReactionListItemInterface): string {
    return item?.author?.['@id'] || `${index}`;
  }
}
